import React from 'react';

import { Box, LayoutBoxProps, layoutInterpolationFn } from '@bojagi/pablo/Box';
import styled, { css } from 'styled-components';
import { Paragraph } from '@bojagi/pablo/Typography';
import useResizeObserver from 'use-resize-observer';
import bgImg from '../../images/visualInteractiveComponents/bg.svg';

const PROGRESS_BAR_COLOR = '#F08E8E';
const DOT_SIZE = 20;

const ProgressBarLineActive = styled.div`
  background-color: ${PROGRESS_BAR_COLOR};
  height: 100%;
  width: 75%;
`;

const ProgressBarLine = styled.div`
  background-color: #d4d4d4;
  height: 9px;
  margin: 8px ${DOT_SIZE * 0.5}px 24px;
  position: relative;
`;

interface ProgressBarDot {
  xPos: number | string;
}

const ProgressBarDot = styled.div<ProgressBarDot>`
  background-color: ${PROGRESS_BAR_COLOR};
  height: ${DOT_SIZE}px;
  width: ${DOT_SIZE}px;
  border-radius: 50%;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25), inset -1px -3px 2px rgba(0, 0, 0, 0.1);
  position: absolute;
  left: ${props => props.xPos};
  top: -${DOT_SIZE * 0.25}px;
  transform: translateX(-50%);
`;

const ComponentWrapper = styled.div`
  max-width: 430px;
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.1);
  background-color: white;
  border-radius: 12px;
  margin-top: 6px;
  margin-left: 40px;
  padding: 24px 24px 30px;
  box-sizing: border-box;
  position: relative;
`;

const Text = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 18px;
  line-height: 21px;
  margin-bottom: 8px;
`;

const Toolbox = styled.div`
  background-color: #1f1f1f;
  padding: 8px 10px;
  border-radius: 8px;
  position: absolute;
  top: 246px;
  max-width: 320px;
  width: 95%;
`;

const StickyNote = styled.div<LayoutBoxProps>`
  background: #fff09e;
  border: 1px solid #ffe347;
  box-sizing: border-box;
  box-shadow: 2px 3px 1px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 16px 12px;
  position: absolute;
  ${layoutInterpolationFn}
`;

interface CommentDotProps extends LayoutBoxProps {
  outerColor: string;
  innerColor: string;
  borderColor: string;
}

const CommentDot = styled.div<CommentDotProps>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => props.outerColor};
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${props => props.borderColor};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2), 0px 2px 5px rgba(0, 0, 0, 0.1);

  :after {
    content: '';
    width: 14px;
    height: 14px;
    background-color: ${props => props.innerColor};
    border-radius: 50%;
  }

  position: absolute;
  ${layoutInterpolationFn}
`;

const AnnotationArrow = ({ length, ...props }) => {
  const marginX = 170;
  const { width = 0, ref } = useResizeObserver<HTMLDivElement>();
  const innerWidth = width - 7;
  const relativeLength = Math.floor(innerWidth * length);

  return (
    <Box
      position="absolute"
      left={120}
      top={89}
      display="flex"
      justifyContent="center"
      width={`calc(100% - ${marginX}px)`}
      ref={ref}
      {...props}
    >
      <svg width="100%" height="36" viewBox={`0 0 ${width} 36`}>
        <g transform={`translate(${innerWidth - relativeLength},0)`}>
          <g filter="url(#filter0_d)">
            <mask
              id="path-1-outside-1"
              maskUnits="userSpaceOnUse"
              x="3.93652"
              y="3.59143"
              width={width}
              height="27"
              fill="black"
            >
              <rect fill="white" x="3.93652" y="3.59143" width={relativeLength - 1} height="27" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d={`M7.37618 17.4649L17.8712 28.0232C18.5153 28.6712 19.6313 28.7347 20.364 28.1653C21.0968 27.5958 21.1687 26.6089 20.5246 25.9609L12.8448 18.2348L12.858 18.0544H${relativeLength}.545V15.0544H13.153L21.8093 8.32651C22.5421 7.75703 22.614 6.77012 21.9699 6.12221C21.3259 5.47429 20.2098 5.41072 19.4771 5.98021L7.53677 15.2606C6.80405 15.8301 6.73215 16.817 7.37618 17.4649Z`}
              />
            </mask>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d={`M7.37618 17.4649L17.8712 28.0232C18.5153 28.6712 19.6313 28.7347 20.364 28.1653C21.0968 27.5958 21.1687 26.6089 20.5246 25.9609L12.8448 18.2348L12.858 18.0544H${relativeLength}.545V15.0544H13.153L21.8093 8.32651C22.5421 7.75703 22.614 6.77012 21.9699 6.12221C21.3259 5.47429 20.2098 5.41072 19.4771 5.98021L7.53677 15.2606C6.80405 15.8301 6.73215 16.817 7.37618 17.4649Z`}
              fill="#E89211"
            />
            <path
              d="M17.8712 28.0232L16.4528 29.4332L16.4528 29.4332L17.8712 28.0232ZM7.37618 17.4649L5.95771 18.8748L5.95772 18.8748L7.37618 17.4649ZM20.5246 25.9609L19.1062 27.3709L19.1062 27.3709L20.5246 25.9609ZM12.8448 18.2348L10.8501 18.0895L10.7839 18.9984L11.4264 19.6448L12.8448 18.2348ZM12.858 18.0544V16.0544H10.9984L10.8633 17.9091L12.858 18.0544ZM255.545 18.0544V20.0544H257.545V18.0544H255.545ZM255.545 15.0544H257.545V13.0544H255.545V15.0544ZM13.153 15.0544L11.9257 13.4753L7.32067 17.0544H13.153V15.0544ZM21.8093 8.32651L20.582 6.74739L20.582 6.74739L21.8093 8.32651ZM21.9699 6.12221L20.5515 7.53216L20.5515 7.53216L21.9699 6.12221ZM19.4771 5.98021L18.2498 4.40108L18.2498 4.40108L19.4771 5.98021ZM7.53677 15.2606L6.30943 13.6814L6.30942 13.6814L7.53677 15.2606ZM19.2897 26.6133L8.79464 16.0549L5.95772 18.8748L16.4528 29.4332L19.2897 26.6133ZM19.1367 26.5861C19.1627 26.5659 19.1834 26.5581 19.192 26.5556C19.1995 26.5534 19.2008 26.5542 19.1981 26.554C19.1954 26.5539 19.2005 26.5534 19.2143 26.5593C19.2292 26.5657 19.2577 26.5811 19.2897 26.6133L16.4528 29.4332C17.8267 30.8154 20.0803 30.9188 21.5914 29.7444L19.1367 26.5861ZM19.1062 27.3709C19.0299 27.2942 18.9519 27.1395 18.9659 26.9472C18.9797 26.7576 19.0746 26.6344 19.1367 26.5861L21.5914 29.7444C23.244 28.4599 23.4588 26.0758 21.9431 24.551L19.1062 27.3709ZM11.4264 19.6448L19.1062 27.3709L21.9431 24.551L14.2633 16.8249L11.4264 19.6448ZM10.8633 17.9091L10.8501 18.0895L14.8395 18.3801L14.8527 18.1998L10.8633 17.9091ZM255.545 16.0544H12.858V20.0544H255.545V16.0544ZM253.545 15.0544V18.0544H257.545V15.0544H253.545ZM13.153 17.0544H255.545V13.0544H13.153V17.0544ZM20.582 6.74739L11.9257 13.4753L14.3804 16.6336L23.0367 9.90564L20.582 6.74739ZM20.5515 7.53216C20.4752 7.45545 20.3972 7.30074 20.4112 7.10845C20.425 6.91886 20.5199 6.79562 20.582 6.74739L23.0367 9.90564C24.6893 8.62121 24.9041 6.23707 23.3884 4.71225L20.5515 7.53216ZM20.7044 7.55934C20.6785 7.57954 20.6578 7.58733 20.6492 7.58985C20.6416 7.59208 20.6404 7.59129 20.643 7.59144C20.6457 7.5916 20.6406 7.59202 20.6269 7.58614C20.612 7.57979 20.5834 7.56431 20.5515 7.53216L23.3884 4.71225C22.0145 3.33005 19.7609 3.22662 18.2498 4.40108L20.7044 7.55934ZM8.76411 16.8397L20.7044 7.55933L18.2498 4.40108L6.30943 13.6814L8.76411 16.8397ZM8.79464 16.0549C8.8709 16.1316 8.94892 16.2863 8.93491 16.4786C8.9211 16.6682 8.82618 16.7914 8.76411 16.8397L6.30942 13.6814C4.65685 14.9659 4.44206 17.35 5.95771 18.8748L8.79464 16.0549Z"
              fill="white"
              mask="url(#path-1-outside-1)"
            />
          </g>
        </g>
        <defs>
          <filter
            id="filter0_d"
            x="0.936035"
            y="0.591675"
            width="260.609"
            height="34.9622"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="2" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
          </filter>
        </defs>
      </svg>
    </Box>
  );
};

const AnnotationCircle = styled.div`
  position: absolute;
  right: 12px;
  top: 17px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #11e8db;
`;

const TruckIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24">
    <path
      d="M17 8h3l3 4.056V18h-2.035a3.5 3.5 0 0 1-6.93 0h-5.07a3.5 3.5 0 0 1-6.93 0H1V6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2zm0 2v3h4v-.285L18.992 10H17z"
      fill="currentColor"
    />
  </svg>
);

const Toolbar = () => (
  <svg width="100%" height="33" viewBox="0 0 320 33" style={{ display: 'block' }}>
    <path
      d="M20.9723 17.6904C21.8051 17.036 22.4782 16.2007 22.9407 15.2479C23.4033 14.2951 23.6432 13.2496 23.6423 12.1904H25.6423C25.6433 13.6009 25.3124 14.9917 24.6762 16.2505C24.04 17.5093 23.1165 18.6007 21.9803 19.4364L24.5083 23.8144C24.7735 24.2738 24.8454 24.8197 24.7081 25.332C24.5708 25.8444 24.2357 26.2812 23.7763 26.5464L20.2493 20.4384C19.112 20.936 17.8837 21.1921 16.6423 21.1904C15.4009 21.1921 14.1726 20.936 13.0353 20.4384L9.50831 26.5464C9.04896 26.2812 8.71378 25.8444 8.57651 25.332C8.43923 24.8197 8.5111 24.2738 8.77631 23.8144L13.8393 15.0444C13.3343 14.5479 12.9701 13.9262 12.7839 13.2428C12.5977 12.5595 12.5962 11.839 12.7795 11.1548C12.9628 10.4707 13.3244 9.8475 13.8274 9.34883C14.3304 8.85016 14.9566 8.4939 15.6423 8.31643V6.19043H17.6423V8.31643C18.328 8.4939 18.9543 8.85016 19.4572 9.34883C19.9602 9.8475 20.3218 10.4707 20.5051 11.1548C20.6884 11.839 20.6869 12.5595 20.5007 13.2428C20.3145 13.9262 19.9503 14.5479 19.4453 15.0444L20.9723 17.6904ZM19.2423 18.6924L17.7143 16.0454C17.0136 16.24 16.2731 16.24 15.5723 16.0454L14.0443 18.6924C14.8483 19.0134 15.7243 19.1904 16.6433 19.1904C17.5613 19.1904 18.4383 19.0134 19.2423 18.6924ZM16.6423 14.1904C17.1727 14.1904 17.6814 13.9797 18.0565 13.6046C18.4316 13.2296 18.6423 12.7209 18.6423 12.1904C18.6423 11.66 18.4316 11.1513 18.0565 10.7762C17.6814 10.4011 17.1727 10.1904 16.6423 10.1904C16.1119 10.1904 15.6032 10.4011 15.2281 10.7762C14.853 11.1513 14.6423 11.66 14.6423 12.1904C14.6423 12.7209 14.853 13.2296 15.2281 13.6046C15.6032 13.9797 16.1119 14.1904 16.6423 14.1904Z"
      fill="white"
    />
    <path
      d="M50.5524 16.5505L53.6424 25.0445L50.8244 26.0705L47.7324 17.5765L43.5604 20.7325L45.0504 5.82349L55.7764 16.2865L50.5524 16.5505Z"
      fill="white"
    />
    <path
      d="M82.6423 26.6904L79.8423 23.1904H74.6423C74.3771 23.1904 74.1227 23.0851 73.9352 22.8975C73.7477 22.71 73.6423 22.4557 73.6423 22.1904V11.2934C73.6423 11.0282 73.7477 10.7739 73.9352 10.5863C74.1227 10.3988 74.3771 10.2934 74.6423 10.2934H90.6423C90.9075 10.2934 91.1619 10.3988 91.3494 10.5863C91.5369 10.7739 91.6423 11.0282 91.6423 11.2934V22.1904C91.6423 22.4557 91.5369 22.71 91.3494 22.8975C91.1619 23.0851 90.9075 23.1904 90.6423 23.1904H85.4423L82.6423 26.6904ZM84.4813 21.1904H89.6423V12.2934H75.6423V21.1904H80.8033L82.6423 23.4884L84.4813 21.1904ZM70.6423 6.19043H87.6423V8.19043H71.6423V19.1904H69.6423V7.19043C69.6423 6.92521 69.7477 6.67086 69.9352 6.48332C70.1227 6.29579 70.3771 6.19043 70.6423 6.19043Z"
      fill="white"
    />
    <line x1="105.142" y1="0.19043" x2="105.142" y2="32.1904" stroke="white" strokeOpacity="0.2" />
    <path
      d="M128.642 26.1904C123.119 26.1904 118.642 21.7134 118.642 16.1904C118.642 10.6674 123.119 6.19043 128.642 6.19043C134.165 6.19043 138.642 10.6674 138.642 16.1904C138.642 21.7134 134.165 26.1904 128.642 26.1904ZM128.642 24.1904C130.764 24.1904 132.799 23.3476 134.299 21.8473C135.799 20.347 136.642 18.3122 136.642 16.1904C136.642 14.0687 135.799 12.0339 134.299 10.5336C132.799 9.03329 130.764 8.19043 128.642 8.19043C126.521 8.19043 124.486 9.03329 122.985 10.5336C121.485 12.0339 120.642 14.0687 120.642 16.1904C120.642 18.3122 121.485 20.347 122.985 21.8473C124.486 23.3476 126.521 24.1904 128.642 24.1904Z"
      fill="white"
    />
    <rect x="148.642" y="4.19043" width="24" height="24" rx="6" fill="#5E41F1" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M155.142 8.69043C154.038 8.69043 153.142 9.58586 153.142 10.6904V21.6904C153.142 22.795 154.038 23.6904 155.142 23.6904H166.142C167.247 23.6904 168.142 22.795 168.142 21.6904V10.6904C168.142 9.58586 167.247 8.69043 166.142 8.69043H155.142ZM155.809 10.3571C155.257 10.3571 154.809 10.8048 154.809 11.3571V21.0238C154.809 21.576 155.257 22.0238 155.809 22.0238H165.476C166.028 22.0238 166.476 21.576 166.476 21.0238V11.3571C166.476 10.8048 166.028 10.3571 165.476 10.3571H155.809Z"
      fill="white"
    />
    <path
      d="M196.646 13.6044L188.039 22.2114L186.625 20.7974L195.231 12.1904H187.646V10.1904H198.646V21.1904H196.646V13.6044Z"
      fill="white"
    />
    <path
      d="M228.37 13.8764L226.956 12.4624L217.642 21.7764V23.1904H219.056L228.37 13.8764ZM229.784 12.4624L231.198 11.0484L229.784 9.63439L228.37 11.0484L229.784 12.4624ZM219.884 25.1904H215.642V20.9474L229.077 7.51239C229.265 7.32492 229.519 7.2196 229.784 7.2196C230.049 7.2196 230.304 7.32492 230.491 7.51239L233.32 10.3414C233.508 10.5289 233.613 10.7832 233.613 11.0484C233.613 11.3136 233.508 11.5679 233.32 11.7554L219.885 25.1904H219.884Z"
      fill="white"
    />
    <path
      d="M249.642 12.1904V24.1904H253.642V12.1904H249.642ZM247.642 11.1904L251.642 6.19043L255.642 11.1904V26.1904H247.642V11.1904ZM263.642 20.1904V18.1904H260.642V16.1904H263.642V14.1904H261.642V12.1904H263.642V10.1904H259.642V24.1904H263.642V22.1904H261.642V20.1904H263.642ZM258.642 8.19043H264.642C264.908 8.19043 265.162 8.29579 265.349 8.48332C265.537 8.67086 265.642 8.92521 265.642 9.19043V25.1904C265.642 25.4557 265.537 25.71 265.349 25.8975C265.162 26.0851 264.908 26.1904 264.642 26.1904H258.642C258.377 26.1904 258.123 26.0851 257.935 25.8975C257.748 25.71 257.642 25.4557 257.642 25.1904V9.19043C257.642 8.92521 257.748 8.67086 257.935 8.48332C258.123 8.29579 258.377 8.19043 258.642 8.19043Z"
      fill="white"
    />
    <line x1="281.142" y1="0.19043" x2="281.142" y2="32.1904" stroke="white" strokeOpacity="0.2" />
    <circle
      cx="304.642"
      cy="16.1904"
      r="11.5"
      stroke="white"
      strokeOpacity="0.5"
      strokeDasharray="2 1"
    />
    <circle cx="304.642" cy="16.1904" r="9" fill="#2DD5AC" />
  </svg>
);

const Background = styled.div`
  position: absolute;
  background-image: url(${bgImg});
  top: -100px;
  left: -40px;
  width: 481px;
  height: 479px;
`;

export function VisualInteractiveComponentsIllustration() {
  return (
    <Box
      width={['90vw', 490]}
      mr={[0, 0, 60]}
      mb={[130, 60, 0]}
      position="relative"
      flexShrink={0}
      css={css`
        user-select: none;
      `}
    >
      <Background />
      <ComponentWrapper>
        <ProgressBarLine>
          <ProgressBarLineActive />
          <ProgressBarDot xPos={0} />
          <ProgressBarDot xPos="50%" />
          <ProgressBarDot xPos="100%" />
        </ProgressBarLine>
        <Text>Your order status:</Text>
        <Box display="flex" justifyContent="space-between">
          <Text>Shipped</Text>
          <TruckIcon />
        </Box>

        <Box borderTop="1px solid #D2D2D2" my={16} />
        <Text>Address:</Text>
        <Text>
          Hans Wurst Straße 32
          <br />
          12345 Berlin
        </Text>
        <AnnotationArrow length={1} />
        <AnnotationCircle />
        <CommentDot
          left={95}
          top={95}
          outerColor="#F6D3A0"
          innerColor="#E89211"
          borderColor="#C07E1A"
        />
        <CommentDot
          right={37}
          top={20}
          outerColor="#ABEEDE"
          innerColor="#40C3A4"
          borderColor="#40C3A4"
        />
        <StickyNote right={-10} top={-40}>
          <Paragraph inline>Change to Gray</Paragraph>
        </StickyNote>
        <StickyNote left={-32} top={120}>
          <Paragraph inline>Move icon here</Paragraph>
        </StickyNote>
      </ComponentWrapper>
      <Toolbox>
        <Toolbar />
      </Toolbox>
    </Box>
  );
}
